import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TRANSFER_FEE_USD } from '@incpt/shared';
import { ScreenBackdrop } from './ScreenBackdrop';
import { BottomSheet } from './BottomSheet';
import { TokenSheet, DEFAULT_COIN, coinKey, type Coin } from './TokenSheet';
import { AddressSheet } from './AddressSheet';
import { ProgressButton } from './ProgressButton';
import { Confetti } from './Confetti';
import { ChevronRight, SwapArrows } from './icons';
import { useBackButton, hasTelegramBackButton } from '../../hooks/useBackButton';
import { useWallet } from '../../hooks/useWallet';
import { useTransfers } from '../../hooks/useTransfers';
import { useFeeQuote } from '../../hooks/useFeeQuote';
import { useBtcSpendQuote } from '../../hooks/useBtcSpendQuote';
import { useMe } from '../../hooks/useMe';
import { ReauthFlow } from './ReauthFlow';
import { formatTokenAmount, formatUsd, DISPLAY_DECIMALS } from '../../lib/format';
import { addressDisplayName } from '../../lib/walletNames';
import { useSwapActions, useSwapAssets, useSwapConfig } from '../../hooks/useSwap';
import type { SwapEstimate, SwapOrderDto } from '../../lib/swap';
import type { TransferDto } from '../../lib/transfers';

const DEBOUNCE_MS = 320;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

type ExecuteSnapshot = {
  fromChain: Coin['chain'];
  fromToken: Coin['symbol'];
  toChain: Coin['chain'];
  toToken: Coin['symbol'];
  amount: string;
  fromAddressId?: string;
  fromIndex?: number;
};

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-[11px]">
      <span className="shrink-0 text-[14px] text-[#8b8a92]">{label}</span>
      <div className="min-w-0 text-right text-[14px] font-medium text-ink">{children}</div>
    </div>
  );
}

function formatRateLine(from: Coin, to: Coin, fromAmount: number, toAmount: number): string {
  if (!(fromAmount > 0) || !(toAmount > 0)) return '—';
  const fromNative = !!from.native;
  const toNative = !!to.native;
  if (toNative && !fromNative) {
    return `1 ${to.symbol} = ${formatTokenAmount(fromAmount / toAmount, from.symbol)} ${from.symbol}`;
  }
  if (fromNative && !toNative) {
    return `1 ${from.symbol} = ${formatTokenAmount(toAmount / fromAmount, to.symbol)} ${to.symbol}`;
  }
  return `1 ${from.symbol} = ${formatTokenAmount(toAmount / fromAmount, to.symbol)} ${to.symbol}`;
}

function sanitizeAmount(raw: string, maxDecimals: number): string {
  let s = raw.replace(',', '.').replace(/[^\d.]/g, '');
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  if (s.startsWith('.')) s = `0${s}`;
  if (maxDecimals === 0) return s.replace(/\./g, '') || '0';
  if (s.includes('.')) {
    const [intPart = '0', frac = ''] = s.split('.');
    s = `${intPart || '0'}.${frac.slice(0, maxDecimals)}`;
  }
  if (s.includes('.')) {
    const [intPart = '0', frac = ''] = s.split('.');
    s = `${intPart.replace(/^0+(?=\d)/, '') || '0'}.${frac}`;
  } else {
    s = s.replace(/^0+(?=\d)/, '') || '0';
  }
  return s;
}

function AssetPill({ coin, onClick }: { coin: Coin; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex shrink-0 items-center gap-2 rounded-full bg-[#f4f5f7] py-[7px] pl-[7px] pr-3 transition-colors active:bg-[#eceef2]"
    >
      <coin.Icon size={30} />
      <span className="flex min-w-0 flex-col items-start leading-tight">
        <span className="text-[15px] font-semibold tracking-tight text-ink">{coin.symbol}</span>
        <span className="max-w-[88px] truncate text-[10px] font-medium text-[#8b8a92]">{coin.network}</span>
      </span>
      <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden className="ml-0.5 opacity-50">
        <path d="M1 1.25 5 4.75 9 1.25" stroke="#121212" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

export function SwapScreen({
  onClose,
  initialFrom,
}: {
  onClose: () => void;
  initialFrom?: Coin;
}) {
  const { t } = useTranslation();
  useBackButton(onClose);
  const tgBack = hasTelegramBackButton();
  const { wallet, balances, byTokenChain, priceBySymbol } = useWallet();
  const { send } = useTransfers();
  const { me, initData, canFetch } = useMe();
  const { data: config } = useSwapConfig();
  const {
    coins: swapCoins,
    loading: assetsLoading,
    error: assetsError,
    ready: assetsReady,
  } = useSwapAssets();
  const { estimate: estimateMut, createOrder, linkDeposit } = useSwapActions();
  const amountInputRef = useRef<HTMLInputElement>(null);

  const [fromCoin, setFromCoin] = useState<Coin>(initialFrom ?? DEFAULT_COIN);
  const [toCoin, setToCoin] = useState<Coin>(DEFAULT_COIN);
  const [amount, setAmount] = useState('0');
  const [picker, setPicker] = useState<'from' | 'to' | null>(null);
  const [selectedAddr, setSelectedAddr] = useState<string | null>(null);
  const [walletOpen, setWalletOpen] = useState(false);
  const [estimate, setEstimate] = useState<SwapEstimate | null>(null);
  const [estimateError, setEstimateError] = useState<string | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reauthOpen, setReauthOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const executingRef = useRef(false);
  const idempotencyRef = useRef<string | null>(null);
  const snapshotRef = useRef<ExecuteSnapshot | null>(null);
  /** After CH create, keep payin so a failed BTC send can retry without a new orphan order. */
  const pendingOrderRef = useRef<SwapOrderDto | null>(null);

  const walletName = wallet?.name ?? 'Main wallet';
  const chainAddresses = (wallet?.addresses ?? []).filter((a) => a.chain === fromCoin.chain);
  const sourceIdx = Math.max(
    0,
    chainAddresses.findIndex((a) => a.eoaAddress === selectedAddr),
  );
  const source = chainAddresses[sourceIdx];
  const sourceName = source ? addressDisplayName(source, t('nav.wallet')) : walletName;

  // Reset source when the from-chain changes (selected address may belong to another chain).
  useEffect(() => {
    setSelectedAddr(null);
  }, [fromCoin.chain]);

  // Clamp the pair into the live provider ∩ catalogue list once it arrives.
  useEffect(() => {
    if (!assetsReady || swapCoins.length === 0) return;
    setFromCoin((prev) => {
      if (swapCoins.some((c) => coinKey(c) === coinKey(prev))) return prev;
      const prefer =
        initialFrom && swapCoins.find((c) => coinKey(c) === coinKey(initialFrom));
      return (
        prefer ??
        swapCoins.find((c) => c.symbol === 'USDT' && c.chain === 'trx') ??
        swapCoins[0]!
      );
    });
  }, [assetsReady, swapCoins, initialFrom]);

  useEffect(() => {
    if (!assetsReady || swapCoins.length === 0) return;
    setToCoin((prev) => {
      const available = swapCoins.filter((c) => coinKey(c) !== coinKey(fromCoin));
      if (available.some((c) => coinKey(c) === coinKey(prev))) return prev;
      return available.find((c) => c.symbol === 'BTC') ?? available[0] ?? swapCoins[0]!;
    });
  }, [assetsReady, swapCoins, fromCoin]);

  const amountNum = Number(amount.replace(',', '.')) || 0;
  const fromBal = balances
    .filter(
      (b) =>
        b.chain === fromCoin.chain &&
        b.token === fromCoin.symbol &&
        b.address === source?.eoaAddress,
    )
    .reduce((sum, b) => sum + Number(b.raw) / 10 ** b.decimals, 0);
  const toBal = byTokenChain[`${toCoin.symbol}:${toCoin.chain}`] ?? 0;
  const fromPrice =
    priceBySymbol?.[fromCoin.symbol] ?? (fromCoin.symbol === 'USDT' || fromCoin.symbol === 'USDC' ? 1 : 0);
  const toPrice =
    priceBySymbol?.[toCoin.symbol] ?? (toCoin.symbol === 'USDT' || toCoin.symbol === 'USDC' ? 1 : 0);
  const fiatFrom = amountNum * fromPrice;
  const toAmountNum = estimate ? Number(estimate.toAmount) : 0;
  const fiatTo = toAmountNum * toPrice;

  // Same planner as Send: fee-quote needs a recipient to run the live Tron
  // disperse path. Payin is unknown until the order is created, so we probe a
  // burn/dead address. API floors Tron swap quotes at fresh 2-leg energy so the
  // probe cannot underquote vs a normal Send. Natives ignore `to`.
  const isNative = !!fromCoin.native;
  const feeProbeTo =
    fromCoin.chain === 'btc'
      ? undefined
      : fromCoin.chain === 'eth'
        ? '0x000000000000000000000000000000000000dEaD'
        : 'T9yD14Nj9j7xAB4dbGeiX9h8unkKHxuWwb';
  const swapFromForFee = !isNative && amountNum > 0 ? amountNum : undefined;
  const {
    feeUsd: feeQuote,
    maxFeeUsd,
    feeNative,
    fresh: feeFresh,
    swapNetworkFree,
  } = useFeeQuote(
    fromCoin.chain,
    fromCoin.symbol,
    feeProbeTo,
    source?.eoaAddress,
    undefined,
    swapFromForFee,
  );
  const isBtc = fromCoin.chain === 'btc';
  const btcSpend = useBtcSpendQuote(
    isBtc ? source?.eoaAddress : undefined,
    isBtc ? source?.publicKey : undefined,
    isBtc && amountNum > 0 ? amountNum : undefined,
  );
  const networkFeeUsd = isBtc
    ? (btcSpend.feeBtc ?? 0) * fromPrice
    : (feeQuote ?? (isNative ? 0 : TRANSFER_FEE_USD));
  // BTC Max/fee come from planBtcMaxSend (same selectUTXO as the real send).
  // Other natives: fee quote is ready once fresh (TRX may be 0 with free bandwidth).
  const feeReady = isBtc
    ? btcSpend.ready
    : feeFresh && (!isNative || feeNative != null);
  const showNetworkFree = !isBtc && swapNetworkFree && feeReady;
  const feeReserveInCoin = showNetworkFree
    ? 0
    : isBtc
      ? (btcSpend.feeBtc ?? 0)
      : isNative
        ? (feeNative ?? 0)
        : (maxFeeUsd ?? networkFeeUsd);
  const maxSendable = isBtc
    ? Math.max(0, btcSpend.maxBtc ?? 0)
    : Math.max(0, fromBal - feeReserveInCoin);
  const feeNativeDisplay = isBtc ? (btcSpend.feeBtc ?? 0) : (feeNative ?? 0);
  const networkFeeLabel = !feeReady
    ? t('send.feeLoading')
    : showNetworkFree
      ? t('swap.networkFeeFree')
      : isNative
        ? `≈ ${formatTokenAmount(feeNativeDisplay, fromCoin.symbol)} ${fromCoin.symbol} ($${networkFeeUsd.toFixed(2)})`
        : maxFeeUsd != null && maxFeeUsd > networkFeeUsd + 0.001
          ? t('send.feeReserveValue', {
              amount: networkFeeUsd.toFixed(2),
              reserve: maxFeeUsd.toFixed(2),
            })
          : `≈ $${networkFeeUsd.toFixed(2)}`;

  const minAmount = estimate?.minAmount ?? 0;
  const belowMin = minAmount > 0 && amountNum > 0 && amountNum < minAmount;
  const overBal = amountNum > maxSendable + 1e-12;
  const sameAsset = fromCoin.symbol === toCoin.symbol && fromCoin.chain === toCoin.chain;
  const swapEnabled = config?.enabled !== false;
  const hasFromAddress = (wallet?.addresses ?? []).some((a) => a.chain === fromCoin.chain);
  const hasToAddress = (wallet?.addresses ?? []).some((a) => a.chain === toCoin.chain);
  const canSwap =
    swapEnabled &&
    assetsReady &&
    !assetsError &&
    swapCoins.length >= 2 &&
    hasFromAddress &&
    hasToAddress &&
    amountNum > 0 &&
    !belowMin &&
    !overBal &&
    !sameAsset &&
    !!estimate &&
    !estimating &&
    feeReady &&
    !!wallet?.id &&
    !submitting &&
    !done;

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const estimateGen = useRef(0);

  const runEstimate = useCallback(
    async (from: Coin, to: Coin, amt: string) => {
      const n = Number(String(amt).replace(',', '.'));
      const gen = ++estimateGen.current;
      if (!assetsReady || assetsError) {
        setEstimate(null);
        setEstimateError(null);
        setEstimating(false);
        return;
      }
      if (!(n > 0) || (from.symbol === to.symbol && from.chain === to.chain)) {
        setEstimate(null);
        setEstimateError(null);
        setEstimating(false);
        return;
      }
      setEstimating(true);
      setEstimateError(null);
      try {
        const res = await estimateMut.mutateAsync({
          fromChain: from.chain,
          fromToken: from.symbol,
          toChain: to.chain,
          toToken: to.symbol,
          fromAmount: String(n),
        });
        if (gen !== estimateGen.current) return;
        setEstimate(res);
      } catch (e) {
        if (gen !== estimateGen.current) return;
        setEstimate(null);
        const msg = e instanceof Error ? e.message : String(e);
        try {
          const m = msg.match(/\{.*\}/);
          if (m) {
            const j = JSON.parse(m[0]) as { error?: string; message?: string; minAmount?: number };
            if (j.error === 'BELOW_MIN') {
              setEstimateError(
                j.message ?? t('swap.belowMin', { min: j.minAmount, token: from.symbol }),
              );
              return;
            }
            if (j.error === 'UNSUPPORTED_ASSET' || j.error === 'SAME_ASSET') {
              setEstimateError(j.message ?? t('swap.assetUnavailable'));
              return;
            }
            if (j.message) {
              setEstimateError(j.message);
              return;
            }
          }
        } catch {
          /* fall through */
        }
        setEstimateError(t('swap.estimateFailed'));
      } finally {
        if (gen === estimateGen.current) setEstimating(false);
      }
    },
    [assetsReady, assetsError, estimateMut.mutateAsync, t],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void runEstimate(fromCoin, toCoin, amount);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [fromCoin, toCoin, amount, runEstimate]);

  const onAmountChange = (raw: string) => {
    pendingOrderRef.current = null;
    setAmount(sanitizeAmount(raw, DISPLAY_DECIMALS[fromCoin.symbol] ?? 8));
  };

  const setPct = (pct: number) => {
    if (!(maxSendable > 0) && pct === 1) return;
    if (!(fromBal > 0)) return;
    const raw = pct === 1 ? maxSendable : fromBal * pct;
    const dec = DISPLAY_DECIMALS[fromCoin.symbol] ?? 8;
    const factor = 10 ** dec;
    const truncated = Math.floor(Math.max(0, raw) * factor + 1e-9) / factor;
    setAmount(String(truncated));
  };

  const flip = () => {
    pendingOrderRef.current = null;
    setFromCoin(toCoin);
    setToCoin(fromCoin);
    setAmount('0');
    setEstimate(null);
  };

  const onPick = (c: Coin) => {
    if (picker === 'from') {
      setFromCoin(c);
      if (c.symbol === toCoin.symbol && c.chain === toCoin.chain) {
        const alt = swapCoins.find((x) => coinKey(x) !== coinKey(c));
        if (alt) setToCoin(alt);
      }
    } else if (picker === 'to') {
      setToCoin(c);
      if (c.symbol === fromCoin.symbol && c.chain === fromCoin.chain) {
        const alt = swapCoins.find((x) => coinKey(x) !== coinKey(c));
        if (alt) setFromCoin(alt);
      }
    }
    setPicker(null);
  };

  const onConfirm = () => {
    if (executingRef.current || submitting || done || !canSwap) return;
    snapshotRef.current = {
      fromChain: fromCoin.chain,
      fromToken: fromCoin.symbol,
      toChain: toCoin.chain,
      toToken: toCoin.symbol,
      amount: String(amountNum),
      fromAddressId: source?.id,
      fromIndex: source?.derivationIndex,
    };
    if (!idempotencyRef.current) idempotencyRef.current = globalThis.crypto.randomUUID();
    setConfirmOpen(false);
    setSubmitError(null);
    if (me?.pinSet || me?.biometricEnabled) {
      setReauthOpen(true);
    } else {
      void executeSwap();
    }
  };

  const executeSwap = async (reauthToken?: string) => {
    if (executingRef.current || done) return;
    const snap = snapshotRef.current;
    if (!wallet?.id || !snap) return;
    executingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      let order = pendingOrderRef.current;
      const samePending =
        order &&
        order.fromChain === snap.fromChain &&
        order.fromToken === snap.fromToken &&
        order.toChain === snap.toChain &&
        order.toToken === snap.toToken &&
        Number(order.fromAmount) === Number(snap.amount) &&
        !!order.payinAddress &&
        (order.status === 'WAITING' || order.status === 'waiting');
      if (!samePending) {
        order = await createOrder.mutateAsync({
          fromChain: snap.fromChain,
          fromToken: snap.fromToken,
          toChain: snap.toChain,
          toToken: snap.toToken,
          fromAmount: snap.amount,
        });
        pendingOrderRef.current = order;
      }
      if (!order?.payinAddress) throw new Error('no_payin');

      const transfer = (await send.mutateAsync({
        walletId: wallet.id,
        chain: snap.fromChain,
        token: snap.fromToken,
        to: order.payinAddress,
        amount: snap.amount,
        fromAddressId: snap.fromAddressId,
        fromIndex: snap.fromIndex,
        swapPartnerOrderId: order.partnerOrderId,
        reauthToken,
        idempotencyKey: idempotencyRef.current ?? undefined,
      })) as TransferDto;

      if (!transfer?.id) throw new Error('transfer_incomplete');

      let linked = false;
      for (let attempt = 0; attempt < 3 && !linked; attempt++) {
        try {
          await linkDeposit.mutateAsync({
            partnerOrderId: order.partnerOrderId,
            depositTransferId: transfer.id,
          });
          linked = true;
        } catch {
          if (attempt < 2) await sleep(400 * (attempt + 1));
        }
      }

      pendingOrderRef.current = null;
      setDone(true);
      setTimeout(onClose, 2200);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (!/transfer_verification_pending|transfer_preparing/i.test(msg)) {
        idempotencyRef.current = null;
      }
      executingRef.current = false;
      setSubmitting(false);
      const friendly = /insufficient_funds|insufficient_balance/i.test(msg)
        ? t('swap.insufficient')
        : msg.slice(0, 180);
      setSubmitError(friendly);
    } finally {
      setReauthOpen(false);
    }
  };

  const rateLine = useMemo(
    () => (estimate ? formatRateLine(fromCoin, toCoin, amountNum, toAmountNum) : '—'),
    [estimate, fromCoin, toCoin, amountNum, toAmountNum],
  );

  const errorText = !swapEnabled
    ? t('swap.disabled')
    : assetsError
      ? t('swap.assetsFailed')
      : assetsLoading && !assetsReady
        ? t('swap.assetsLoading')
        : assetsReady && swapCoins.length < 2
          ? t('swap.assetUnavailable')
          : !hasFromAddress || !hasToAddress
            ? t('swap.noAddress', {
                network: !hasToAddress ? toCoin.network : fromCoin.network,
              })
            : submitError
              ? submitError
              : overBal
                ? t('swap.insufficient')
                : belowMin
                  ? t('swap.belowMin', { min: minAmount, token: fromCoin.symbol })
                  : estimateError;

  const receiveDisplay = estimating
    ? '…'
    : estimate
      ? formatTokenAmount(toAmountNum, toCoin.symbol)
      : '0';

  return (
    <div className="relative flex h-full min-h-full flex-col bg-surface">
      <ScreenBackdrop height={480} fadeStart={38} />
      {done && <Confetti />}

      <div className="relative z-10 flex h-full min-h-full flex-col px-[18px] pb-[calc(var(--tg-safe-bottom)_+_1.5rem)] pt-[calc(var(--tg-safe-top)_+_0.35rem)]">
        {/* Header — back + source wallet */}
        <header className="relative flex h-12 items-center justify-center">
          {!tgBack && (
            <button
              type="button"
              onClick={onClose}
              className="absolute left-0 z-10 flex items-center gap-0.5 text-[17px] font-medium text-[#377DFF] active:opacity-65"
            >
              <ChevronRight size={18} className="rotate-180" />
              {t('common.back')}
            </button>
          )}
          <button
            type="button"
            onClick={() => setWalletOpen(true)}
            className="flex items-center gap-1.5 rounded-full bg-white/90 py-1.5 pl-1.5 pr-3 shadow-[0_6px_20px_-10px_rgba(17,24,39,0.35)] backdrop-blur-sm active:scale-[0.98]"
          >
            <fromCoin.Icon size={24} />
            <span className="max-w-[140px] truncate text-[14px] font-medium text-ink">{sourceName}</span>
            <ChevronRight size={14} className="rotate-90 text-muted" />
          </button>
        </header>

        <div className="mt-5 flex flex-1 flex-col">
          <p className="mb-3 text-center text-[17px] font-semibold tracking-[-0.01em] text-ink">
            {t('swap.title')}
          </p>
          {/* Pair stack */}
          <div className="relative">
            {/* Give */}
            <div className="rounded-[26px] bg-white px-4 pb-[22px] pt-[18px] shadow-[0_12px_40px_-28px_rgba(17,24,39,0.35)]">
              <div className="mb-[18px] flex items-baseline justify-between gap-3">
                <span className="text-[13px] font-medium text-[#9a99a1]">{t('swap.youGive')}</span>
                <button
                  type="button"
                  onClick={() => setPct(1)}
                  disabled={!(maxSendable > 0)}
                  className="truncate text-[12px] text-[#9a99a1] underline decoration-[#9a99a1]/40 underline-offset-2 active:opacity-70 disabled:no-underline disabled:opacity-40"
                >
                  {t('swap.available')}: {formatTokenAmount(fromBal, fromCoin.symbol)} {fromCoin.symbol}
                </button>
              </div>
              <div className="flex items-center gap-3">
                <AssetPill coin={fromCoin} onClick={() => setPicker('from')} />
                <div className="min-w-0 flex-1">
                  <input
                    ref={amountInputRef}
                    type="text"
                    inputMode="decimal"
                    enterKeyHint="done"
                    value={amount}
                    onChange={(e) => onAmountChange(e.target.value)}
                    onFocus={(e) => {
                      if (amount === '0') e.target.select();
                    }}
                    className="w-full bg-transparent text-right text-[36px] font-semibold leading-none tracking-[-0.03em] tabular-nums text-ink outline-none caret-[#377DFF]"
                    placeholder="0"
                    aria-label={t('swap.youGive')}
                  />
                  <div className="mt-2 text-right text-[13px] tabular-nums text-[#9a99a1]">
                    ≈ ${formatUsd(fiatFrom)}
                  </div>
                </div>
              </div>
            </div>

            {/* Flip */}
            <button
              type="button"
              onClick={flip}
              className="absolute left-1/2 top-1/2 z-20 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-[4px] border-surface bg-white shadow-[0_6px_18px_-6px_rgba(55,125,255,0.45)] transition-transform active:scale-95"
              aria-label={t('swap.flip')}
            >
              <SwapArrows size={18} className="text-[#377DFF]" />
            </button>

            {/* Get */}
            <div className="mt-2.5 rounded-[26px] bg-white px-4 pb-[22px] pt-[18px] shadow-[0_12px_40px_-28px_rgba(17,24,39,0.35)]">
              <div className="mb-[18px] flex items-baseline justify-between gap-3">
                <span className="text-[13px] font-medium text-[#9a99a1]">{t('swap.youGet')}</span>
                <span className="truncate text-[12px] text-[#9a99a1]">
                  {t('swap.balance')}: {formatTokenAmount(toBal, toCoin.symbol)} {toCoin.symbol}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <AssetPill coin={toCoin} onClick={() => setPicker('to')} />
                <div className="min-w-0 flex-1 text-right">
                  <div
                    className={`text-[36px] font-semibold leading-none tracking-[-0.03em] tabular-nums ${
                      estimate && toAmountNum > 0 ? 'text-ink' : 'text-[#c4c3c9]'
                    }`}
                  >
                    {receiveDisplay}
                  </div>
                  <div className="mt-2 text-[13px] tabular-nums text-[#9a99a1]">
                    ≈ ${formatUsd(fiatTo)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Percents */}
          <div className="mt-4 flex gap-2">
            {[
              { label: '25%', pct: 0.25 },
              { label: '50%', pct: 0.5 },
              { label: '75%', pct: 0.75 },
              { label: t('swap.max'), pct: 1 },
            ].map((b) => (
              <button
                key={b.label}
                type="button"
                onClick={() => setPct(b.pct)}
                className="h-[42px] flex-1 rounded-[14px] bg-[#e9eaef] text-[14px] font-semibold text-ink transition-colors active:bg-[#dcdee5]"
              >
                {b.label}
              </button>
            ))}
          </div>

          {/* Details */}
          <div className="mt-4 rounded-[26px] bg-white px-4 py-2 shadow-[0_12px_40px_-28px_rgba(17,24,39,0.28)]">
            <DetailRow label={t('swap.rate')}>
              <span className="tabular-nums">{rateLine}</span>
            </DetailRow>
            <DetailRow label={t('swap.networkFee')}>
              {showNetworkFree ? (
                <span className="rounded-full bg-[#e8f8ef] px-2.5 py-[3px] text-[12px] font-semibold text-[#1a9d55]">
                  {t('swap.networkFeeFree')}
                </span>
              ) : (
                <span className="tabular-nums">{networkFeeLabel}</span>
              )}
            </DetailRow>
            <DetailRow label={t('swap.exchangeFee')}>
              <span className="rounded-full bg-[#eaf1ff] px-2.5 py-[3px] text-[12px] font-semibold text-[#377DFF]">
                {t('swap.feeInRate')}
              </span>
            </DetailRow>
            <DetailRow label={t('swap.youReceive')}>
              <span className="tabular-nums">
                {estimate
                  ? `${formatTokenAmount(toAmountNum, toCoin.symbol)} ${toCoin.symbol}`
                  : '—'}
              </span>
            </DetailRow>
          </div>

          {errorText ? (
            <p className="mt-3 text-center text-[13px] font-medium text-[#ff3b30]">{errorText}</p>
          ) : null}

          <p className="mt-5 px-4 text-center text-[12px] leading-[1.45] text-[#9a99a1]">
            {t('swap.hint')}
          </p>

          <div className="mt-auto pt-8">
            <ProgressButton
              active={submitting}
              done={done}
              onClick={() => {
                amountInputRef.current?.blur();
                setConfirmOpen(true);
              }}
              disabled={!canSwap || done}
              className={`flex h-[54px] w-full items-center justify-center rounded-[18px] text-[16px] font-semibold text-white transition-colors ${
                submitting || done
                  ? 'pointer-events-none bg-ink'
                  : canSwap
                    ? 'bg-ink active:opacity-90'
                    : 'pointer-events-none bg-[#c8cad1]'
              }`}
            >
              {submitting ? t('swap.swapping') : t('swap.submit')}
            </ProgressButton>
          </div>
        </div>
      </div>

      <TokenSheet
        open={picker != null}
        onClose={() => setPicker(null)}
        title={picker === 'to' ? t('swap.selectReceive') : t('swap.selectGive')}
        onSelect={onPick}
        coinsOverride={swapCoins}
        searchable
      />

      <AddressSheet
        open={walletOpen}
        onClose={() => setWalletOpen(false)}
        coin={fromCoin}
        title={t('send.selectWallet')}
        onSelect={(addr) => {
          setSelectedAddr(addr);
          setWalletOpen(false);
        }}
      />

      <BottomSheet open={confirmOpen} onClose={() => setConfirmOpen(false)} title={t('swap.confirmTitle')}>
        <div className="mt-5 flex flex-col">
          <DetailRow label={t('swap.youGive')}>
            {amount} {fromCoin.symbol}
            <span className="ml-1 font-normal text-[#8b8a92]">· {fromCoin.network}</span>
          </DetailRow>
          <DetailRow label={t('swap.youGet')}>
            {estimate ? (
              <>
                {formatTokenAmount(toAmountNum, toCoin.symbol)} {toCoin.symbol}
                <span className="ml-1 font-normal text-[#8b8a92]">· {toCoin.network}</span>
              </>
            ) : (
              '—'
            )}
          </DetailRow>
          <DetailRow label={t('swap.rate')}>{rateLine}</DetailRow>
          <DetailRow label={t('swap.networkFee')}>
            {showNetworkFree ? (
              <span className="rounded-full bg-[#e8f8ef] px-2.5 py-[3px] text-[12px] font-semibold text-[#1a9d55]">
                {t('swap.networkFeeFree')}
              </span>
            ) : (
              <span className="tabular-nums">{networkFeeLabel}</span>
            )}
          </DetailRow>
          <DetailRow label={t('swap.exchangeFee')}>{t('swap.feeInRate')}</DetailRow>
        </div>
        <div className="mt-6 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setConfirmOpen(false)}
            className="h-14 flex-[2] rounded-full bg-[#f2f3f4] text-[15px] font-medium text-ink active:bg-black/[0.06]"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting || done || executingRef.current}
            className={`flex h-14 flex-[3] items-center justify-center rounded-full bg-ink text-[15px] font-semibold text-white ${
              submitting || done ? 'opacity-60' : 'active:opacity-90'
            }`}
          >
            {t('swap.submit')}
          </button>
        </div>
      </BottomSheet>

      {reauthOpen && canFetch && (
        <div className="push-enter fixed inset-0 z-[60] overflow-y-auto bg-holo">
          <ReauthFlow
            initData={initData}
            biometricEnabled={!!me?.biometricEnabled}
            reason={t('reauth.reasonSwap')}
            onSuccess={(token) => {
              setReauthOpen(false);
              void executeSwap(token);
            }}
            onCancel={() => setReauthOpen(false)}
          />
        </div>
      )}
    </div>
  );
}
